(() => {
  const rootSelector = "[data-aeocky-cart-page]";
  const initializedAttribute = "data-ae-cart-page-initialized";

  const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function getSection(root) {
    return root?.closest?.(rootSelector) || document.querySelector(rootSelector);
  }

  function getActiveDiscountCodes(section) {
    const raw = section.dataset.discountCodes || "";
    return raw
      .split(",")
      .map((code) => code.trim())
      .filter(Boolean);
  }

  async function fetchCartJson() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: "application/json" } });
    if (!response.ok) {
      throw new Error("Failed to load cart state.");
    }
    return response.json();
  }

  async function fetchCartDiscountCodes() {
    try {
      const cart = await fetchCartJson();
      return (cart.discount_codes || [])
        .filter((discount) => discount && discount.applicable !== false)
        .map((discount) => String(discount.code || "").trim())
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  function setCouponError(form, message) {
    const error = form.querySelector("[data-ae-cart-coupon-error]");
    if (!error) {
      return;
    }

    if (message) {
      error.hidden = false;
      error.textContent = message;
    } else {
      error.hidden = true;
      error.textContent = "";
    }
  }

  async function updateDiscountCodes(section, nextCodes, form) {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ discount: nextCodes.join(",") })
    });

    const payload = await response.json().catch(() => ({}));
    const hadError = !response.ok || payload?.discount_codes?.some((discount) => discount?.applicable === false);

    if (hadError) {
      setCouponError(form, window.themeVariables?.strings?.cartDiscountError || "That coupon code could not be applied.");
      return false;
    }

    section.dataset.discountCodes = nextCodes.join(",");
    window.location.reload();
    return true;
  }

  function syncQuantityButtons(lineItemQuantity) {
    if (!lineItemQuantity) {
      return;
    }

    const input = lineItemQuantity.querySelector("[data-line-key]");
    const decrease = lineItemQuantity.querySelector('[data-ae-qty-adjust="decrease"]');
    const increase = lineItemQuantity.querySelector('[data-ae-qty-adjust="increase"]');

    if (!input || !decrease || !increase) {
      return;
    }

    const min = toNumber(input.min, 0);
    const max = input.max === "" ? Infinity : toNumber(input.max, Infinity);
    const value = toNumber(input.value, min);

    decrease.disabled = input.disabled || value <= min;
    increase.disabled = input.disabled || value >= max;
  }

  function initQuantityControls(section) {
    section.querySelectorAll("line-item-quantity").forEach(syncQuantityButtons);

    section.addEventListener("click", (event) => {
      const button = event.target.closest?.("[data-ae-qty-adjust]");
      if (!button || !section.contains(button) || button.disabled) {
        return;
      }

      const lineItemQuantity = button.closest("line-item-quantity");
      const input = lineItemQuantity?.querySelector("[data-line-key]");
      if (!input || input.disabled) {
        return;
      }

      const min = toNumber(input.min, 0);
      const max = input.max === "" ? Infinity : toNumber(input.max, Infinity);
      const step = toNumber(input.step, 1);
      const value = toNumber(input.value, min);
      const direction = button.dataset.aeQtyAdjust === "increase" ? 1 : -1;
      const nextValue = clamp(value + step * direction, min, max);

      if (nextValue === value) {
        syncQuantityButtons(lineItemQuantity);
        return;
      }

      input.value = String(nextValue);
      syncQuantityButtons(lineItemQuantity);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });

    section.addEventListener("change", (event) => {
      const input = event.target.closest?.("[data-line-key]");
      if (!input || !section.contains(input)) {
        return;
      }

      syncQuantityButtons(input.closest("line-item-quantity"));
    });
  }

  async function handleCouponSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const section = form.closest(rootSelector);
    const input = form.querySelector("[data-ae-cart-coupon-input]");

    if (!section || !input) {
      return;
    }

    const code = input.value.trim();
    if (!code) {
      setCouponError(form, "Enter a coupon code first.");
      return;
    }

    setCouponError(form, "");

    const currentCodes = new Set(await fetchCartDiscountCodes());
    currentCodes.add(code);

    await updateDiscountCodes(section, [...currentCodes], form);
  }

  async function handleCouponRemove(event) {
    const button = event.target.closest?.("[data-ae-cart-discount-remove]");
    if (!button) {
      return;
    }

    const section = button.closest(rootSelector);
    const form = section?.querySelector("[data-ae-cart-coupon-form]");
    if (!section || !form) {
      return;
    }

    event.preventDefault();

    const removeCode = String(button.dataset.aeCartDiscountRemove || "").trim().toLowerCase();
    const currentCodes = (await fetchCartDiscountCodes()).filter((code) => code.toLowerCase() !== removeCode);

    await updateDiscountCodes(section, currentCodes, form);
  }

  function handleDiscountToggle(event) {
    const button = event.target.closest?.("[data-ae-discount-toggle]");
    if (!button) {
      return;
    }

    const panelId = button.getAttribute("aria-controls");
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) {
      return;
    }

    event.preventDefault();
    const expanded = button.getAttribute("aria-expanded") === "true";
    button.setAttribute("aria-expanded", String(!expanded));
    panel.hidden = expanded;
  }

  function getCarouselCards(rail) {
    return Array.from(rail.children).filter((child) => child.matches?.(".ae-cart-page__product-card, .ae-cart-page__category-card"));
  }

  function syncDesktopCarouselFillers(rail) {
    const key = rail.getAttribute("data-ae-carousel");
    const isDesktopRecommendation = window.matchMedia("(min-width: 1024px)").matches && (key === "compatible" || key === "pair-products");
    const cards = getCarouselCards(rail);
    const remainder = cards.length % 4;
    const desiredCount = isDesktopRecommendation && cards.length > 4 && remainder > 0 ? 4 - remainder : 0;
    const currentFillers = Array.from(rail.querySelectorAll(":scope > [data-ae-carousel-filler]"));

    if (currentFillers.length === desiredCount) {
      return;
    }

    currentFillers.forEach((filler) => filler.remove());

    for (let index = 0; index < desiredCount; index += 1) {
      const filler = document.createElement("span");
      filler.className = "ae-cart-page__carousel-filler";
      filler.dataset.aeCarouselFiller = "";
      filler.setAttribute("aria-hidden", "true");
      rail.append(filler);
    }
  }

  function getCarouselPageSize(rail) {
    const cards = getCarouselCards(rail);
    const firstCard = cards[0];
    if (!firstCard) {
      return 1;
    }

    const cardWidth = firstCard.getBoundingClientRect().width;
    const styles = window.getComputedStyle(rail);
    const gap = toNumber(styles.columnGap || styles.gap, 0);
    const pageSize = Math.max(1, Math.floor((rail.clientWidth + gap + 1) / (cardWidth + gap)));

    return Math.min(cards.length, pageSize);
  }

  function getCarouselPageCount(rail) {
    const cards = getCarouselCards(rail);
    const pageSize = getCarouselPageSize(rail);
    return Math.max(1, Math.ceil(cards.length / pageSize));
  }

  function getCarouselPage(rail) {
    const cards = getCarouselCards(rail);
    if (cards.length === 0) {
      return 0;
    }

    const firstCard = cards[0];
    const styles = window.getComputedStyle(rail);
    const gap = toNumber(styles.columnGap || styles.gap, 0);
    const cardWidth = firstCard.getBoundingClientRect().width;
    const pageSize = getCarouselPageSize(rail);
    const pageSpan = (cardWidth + gap) * pageSize;

    if (pageSpan <= 0) {
      return 0;
    }

    return clamp(Math.round(rail.scrollLeft / pageSpan), 0, getCarouselPageCount(rail) - 1);
  }

  function getDotsHost(section, key, fallbackUi) {
    return (
      section.querySelector(`[data-ae-carousel-ui="${key}-dots"]`) ||
      fallbackUi?.querySelector?.("[data-ae-carousel-dots]") ||
      fallbackUi
    );
  }

  function getCarouselDotCount(pageCount) {
    return pageCount;
  }

  function buildCarouselDots(section, key, rail, ui) {
    const dotsHost = getDotsHost(section, key, ui);
    const dots = dotsHost?.querySelector?.("[data-ae-carousel-dots]") || dotsHost;
    if (!dots || !dots.matches?.("[data-ae-carousel-dots]")) {
      return;
    }

    const pageCount = getCarouselPageCount(rail);
    const dotCount = getCarouselDotCount(pageCount);

    dots.replaceChildren(
      ...Array.from({ length: dotCount }, (_, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("aria-label", `Show page ${index + 1} of ${dotCount}`);
        button.dataset.aeCarouselDot = String(index);
        button.addEventListener("click", () => {
          scrollCarouselToPage(rail, index);
        });
        return button;
      })
    );

    const dotsAreHidden = dotCount <= 1;
    dots.hidden = dotsAreHidden;
    if (dotsHost !== dots) {
      dotsHost.hidden = dotsAreHidden;
    }
  }

  function syncCarouselDots(section, key, rail, ui) {
    const dotsHost = getDotsHost(section, key, ui);
    const dots = dotsHost?.querySelector?.("[data-ae-carousel-dots]") || dotsHost;
    if (!dots || !dots.matches?.("[data-ae-carousel-dots]")) {
      return;
    }

    const pageCount = getCarouselPageCount(rail);
    const dotCount = getCarouselDotCount(pageCount);
    const currentPage = getCarouselPage(rail);

    const dotsAreHidden = dotCount <= 1;
    dots.hidden = dotsAreHidden;
    if (dotsHost !== dots) {
      dotsHost.hidden = dotsAreHidden;
    }
    dots.querySelectorAll("button").forEach((button, index) => {
      button.setAttribute("aria-current", index === currentPage ? "true" : "false");
    });
  }

  function scrollCarouselToPage(rail, page) {
    const cards = getCarouselCards(rail);
    if (!cards.length) {
      return;
    }

    const firstCard = cards[0];
    const styles = window.getComputedStyle(rail);
    const gap = toNumber(styles.columnGap || styles.gap, 0);
    const cardWidth = firstCard.getBoundingClientRect().width;
    const pageSize = getCarouselPageSize(rail);
    const pageCount = getCarouselPageCount(rail);
    const targetPage = clamp(page, 0, pageCount - 1);
    const targetCardIndex = clamp(targetPage * pageSize, 0, cards.length - 1);
    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const railRect = rail.getBoundingClientRect();
    const targetCardRect = cards[targetCardIndex].getBoundingClientRect();
    const targetScrollLeft = clamp(targetCardRect.left - railRect.left + rail.scrollLeft, 0, maxScrollLeft);
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    rail.scrollTo({ left: targetScrollLeft, behavior: prefersReducedMotion ? "auto" : "smooth" });
  }

  function syncCarouselButtons(rail, ui) {
    const prev = ui?.querySelector?.("[data-ae-carousel-prev]");
    const next = ui?.querySelector?.("[data-ae-carousel-next]");
    const pageCount = getCarouselPageCount(rail);
    const currentPage = getCarouselPage(rail);
    const hasMultiplePages = pageCount > 1;

    if (ui) {
      ui.hidden = !hasMultiplePages;
    }

    if (prev) {
      prev.disabled = !hasMultiplePages || currentPage <= 0;
    }

    if (next) {
      next.disabled = !hasMultiplePages || currentPage >= pageCount - 1;
    }
  }

  function initCarousels(section) {
    const rails = Array.from(section.querySelectorAll("[data-ae-carousel]"));
    const resizeTargets = new Map();

    const refresh = () => {
      rails.forEach((rail) => {
        const key = rail.getAttribute("data-ae-carousel");
        const ui = section.querySelector(`[data-ae-carousel-ui="${key}"]`);
        if (!ui) {
          return;
        }

        syncDesktopCarouselFillers(rail);
        buildCarouselDots(section, key, rail, ui);
        syncCarouselDots(section, key, rail, ui);
        syncCarouselButtons(rail, ui);
      });
    };

    rails.forEach((rail) => {
      const key = rail.getAttribute("data-ae-carousel");
      const ui = section.querySelector(`[data-ae-carousel-ui="${key}"]`);
      if (!ui) {
        return;
      }

      const prev = ui.querySelector("[data-ae-carousel-prev]");
      const next = ui.querySelector("[data-ae-carousel-next]");
      const scrollHandler = () => {
        syncCarouselDots(section, key, rail, ui);
        syncCarouselButtons(rail, ui);
      };

      syncDesktopCarouselFillers(rail);
      buildCarouselDots(section, key, rail, ui);
      syncCarouselDots(section, key, rail, ui);
      syncCarouselButtons(rail, ui);

      prev?.addEventListener("click", () => scrollCarouselToPage(rail, getCarouselPage(rail) - 1));
      next?.addEventListener("click", () => scrollCarouselToPage(rail, getCarouselPage(rail) + 1));
      rail.addEventListener("scroll", scrollHandler, { passive: true });

      const canDrag = () => rail.scrollWidth > rail.clientWidth + 1;
      if (canDrag()) {
        let pointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let dragging = false;

        rail.addEventListener("pointerdown", (event) => {
          if (event.pointerType !== "mouse" || event.button !== 0 || event.target.closest("button, a, input, textarea, select")) {
            return;
          }

          pointerId = event.pointerId;
          startX = event.clientX;
          startScrollLeft = rail.scrollLeft;
          dragging = false;
          rail.classList.add("is-dragging");
          rail.setPointerCapture?.(event.pointerId);
        });

        rail.addEventListener("pointermove", (event) => {
          if (pointerId !== event.pointerId) {
            return;
          }

          const deltaX = event.clientX - startX;
          if (!dragging && Math.abs(deltaX) < 4) {
            return;
          }

          dragging = true;
          rail.scrollLeft = startScrollLeft - deltaX;
          event.preventDefault();
        });

        const releaseDrag = (event) => {
          if (pointerId !== event.pointerId) {
            return;
          }

          try {
            rail.releasePointerCapture?.(pointerId);
          } catch (error) {
            // Ignore capture release errors during teardown.
          }
          rail.classList.remove("is-dragging");
          pointerId = null;
        };

        rail.addEventListener("pointerup", releaseDrag);
        rail.addEventListener("pointercancel", releaseDrag);
        rail.addEventListener("click", (event) => {
          if (dragging) {
            event.preventDefault();
            event.stopImmediatePropagation();
            dragging = false;
          }
        }, true);
      }

      resizeTargets.set(rail, () => {
        syncDesktopCarouselFillers(rail);
        buildCarouselDots(section, key, rail, ui);
        syncCarouselDots(section, key, rail, ui);
        syncCarouselButtons(rail, ui);
      });
    });

    const resizeHandler = () => {
      resizeTargets.forEach((callback) => callback());
    };

    window.addEventListener("resize", resizeHandler, { passive: true });

    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }

  function setRecommendationButtonState(button, state, message) {
    if (!button) {
      return;
    }

    const labelMap = {
      idle: button.dataset.defaultLabel || button.textContent.trim(),
      adding: button.dataset.addingLabel || "Adding...",
      added: button.dataset.addedLabel || "Added",
      error: button.dataset.errorLabel || "Error"
    };

    if (!button.dataset.defaultLabel) {
      button.dataset.defaultLabel = button.textContent.trim();
    }

    button.textContent = labelMap[state];
    button.setAttribute("data-ae-cart-recommendation-state", state);
    button.setAttribute("aria-busy", state === "adding" ? "true" : "false");
    button.disabled = state === "adding";

    const card = button.closest("[data-ae-cart-product-card]");
    if (card) {
      if (state === "idle") {
        card.removeAttribute("data-ae-cart-card-state");
      } else {
        card.setAttribute("data-ae-cart-card-state", state);
      }
    }

    const form = button.closest("[data-ae-cart-recommendation-form]");
    const status = form?.querySelector("[data-ae-cart-recommendation-status]");
    if (status) {
      if (message) {
        status.hidden = false;
        status.textContent = message;
      } else {
        status.hidden = true;
        status.textContent = "";
      }
    }
  }

  async function handleRecommendationSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    if (form.dataset.submitting === "true") {
      return;
    }

    const button = form.querySelector("[data-ae-cart-add-button]");
    const section = form.closest(rootSelector);
    if (!button || !section) {
      return;
    }

    form.dataset.submitting = "true";
    setRecommendationButtonState(button, "adding", "Adding item to cart.");

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        },
        body: new FormData(form)
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.description || "Unable to add this item.");
      }

      try {
        const cart = await fetchCartJson();
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "recommendation:add",
              cart
            }
          })
        );
      } catch (cartError) {
        // The item has already been added; if the cart refresh fails we still
        // let the page reload so the browser state stays correct.
      }

      setRecommendationButtonState(button, "added", "Added to cart.");

      window.setTimeout(() => {
        window.location.reload();
      }, 900);
    } catch (error) {
      try {
        form.submit();
        return;
      } catch (fallbackError) {
        setRecommendationButtonState(button, "error", error?.message || "Could not add this item.");
        window.setTimeout(() => {
          if (button.isConnected) {
            setRecommendationButtonState(button, "idle");
          }
        }, 1800);
      }
    } finally {
      form.dataset.submitting = "false";
    }
  }

  function initRecommendationForms(section) {
    section.querySelectorAll("[data-ae-cart-recommendation-form]").forEach((form) => {
      form.addEventListener("submit", handleRecommendationSubmit);
    });
  }

  function initSection(section) {
    if (section.hasAttribute(initializedAttribute)) {
      return;
    }

    section.setAttribute(initializedAttribute, "true");

    const couponForm = section.querySelector("[data-ae-cart-coupon-form]");
    couponForm?.addEventListener("submit", handleCouponSubmit);
    section.addEventListener("click", handleCouponRemove);
    section.addEventListener("click", handleDiscountToggle);

    initQuantityControls(section);
    initRecommendationForms(section);
    initCarousels(section);
  }

  async function renderFilledCartPage(section) {
    const sectionId = section?.dataset?.sectionId;
    if (!sectionId) {
      throw new Error("Cart section ID is unavailable.");
    }

    const response = await fetch(
      `${window.Shopify.routes.root}cart?section_id=${encodeURIComponent(sectionId)}`,
      {
        cache: "no-store",
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      }
    );

    if (!response.ok) {
      throw new Error("Failed to refresh the cart page section.");
    }

    const updatedCartPage = new DOMParser()
      .parseFromString(await response.text(), "text/html")
      .querySelector(rootSelector);

    if (!updatedCartPage) {
      throw new Error("Updated cart page markup is unavailable.");
    }

    section.replaceWith(updatedCartPage);
    initSection(updatedCartPage);
  }

  async function reloadWhenEmptyCartBecomesFilled(event) {
    const emptyCartPage = document.querySelector(`${rootSelector}[data-cart-state="empty"]`);
    const itemCount = toNumber(event.detail?.cart?.item_count, 0);

    if (emptyCartPage && itemCount > 0) {
      if (event.detail?.source === "aeocky-quick-add") {
        try {
          await renderFilledCartPage(emptyCartPage);
        } catch (error) {
          console.error("AEOCKY cart page: unable to refresh after quick add", error);
        }
        return;
      }

      window.location.reload();
    }
  }

  document.addEventListener("cart:change", reloadWhenEmptyCartBecomesFilled);
  document.querySelectorAll(rootSelector).forEach(initSection);
})();
